import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useAuthStore } from '@/stores/auth'
import { BarChart3, User, Lock, AlertCircle, Clock } from 'lucide-react'
import { useColors } from '@/lib/utils'
import { useThemeStore } from '@/stores/theme'

export const Route = createFileRoute('/login')({
  beforeLoad: async () => {
    const { checkSession, isInitialized, initialize } = useAuthStore.getState()
    
    // Initialize auth state if not already done
    if (!isInitialized) {
      await initialize()
    }
    
    const { isAuthenticated: currentAuth } = useAuthStore.getState()
    
    if (currentAuth && checkSession()) {
      throw redirect({
        to: '/dashboard',
      })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const { login, isLoading, error, isInitialized, initialize } = useAuthStore()
  const { isDarkMode } = useThemeStore()
  const Colors = useColors(isDarkMode)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [localError, setLocalError] = useState('')
  const [showSessionExpiredMessage, setShowSessionExpiredMessage] = useState(false)

  // Initialize auth state on component mount
  useEffect(() => {
    if (!isInitialized) {
      initialize()
    }
  }, [isInitialized, initialize])

  // Check if user was redirected here due to session expiry
  useEffect(() => {
    const wasLoggedIn = window.localStorage.getItem('wasAuthenticated')
    if (wasLoggedIn === 'true') {
      setShowSessionExpiredMessage(true)
      window.localStorage.removeItem('wasAuthenticated')
      
      // Hide the message after 5 seconds
      setTimeout(() => {
        setShowSessionExpiredMessage(false)
      }, 5000)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError('')
    setShowSessionExpiredMessage(false)

    if (!username || !password) {
      setLocalError('Please fill in all fields')
      return
    }

    if (username.length < 3) {
      setLocalError('Username must be at least 3 characters')
      return
    }

    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters')
      return
    }

    try {
      await login(username, password)
      // Small delay to ensure state is updated before navigation
      setTimeout(() => {
        navigate({ to: '/dashboard' })
      }, 100)
    } catch (error) {
      // Error is handled by the store
    }
  }

  const handleContactAdministrator = () => {
    window.open('https://support.restronaut.ai/submit', '_blank')
  }

  const displayError = error || localError

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: Colors.background.default }}
    >
      <div 
        className="w-full max-w-md p-6 rounded-2xl shadow-lg"
        style={{ 
          backgroundColor: Colors.background.paper,
          boxShadow: isDarkMode 
            ? '0 4px 6px rgba(0, 0, 0, 0.2)' 
            : '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Logo */}
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center space-x-3">
            <BarChart3 size={36} color={Colors.primary.main} strokeWidth={2.5} />
            <div>
              <h1 className="text-xl font-inter-bold" style={{ color: Colors.text.primary }}>
                Falcon Holdings
              </h1>
              <p className="text-sm font-inter-regular" style={{ color: Colors.text.secondary }}>
                Business Intelligence
              </p>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-inter-bold text-center mb-1" style={{ color: Colors.text.primary }}>
          Welcome Back
        </h2>
        <p className="text-center mb-6 font-inter-regular" style={{ color: Colors.text.secondary }}>
          Sign in to continue to your dashboard
        </p>

        {/* Session Expired Message */}
        {showSessionExpiredMessage && (
          <div 
            className="flex items-center space-x-2 p-3 mb-4 rounded-md border"
            style={{ 
              backgroundColor: Colors.warning.main + '10', 
              borderColor: Colors.warning.main 
            }}
          >
            <Clock size={16} color={Colors.warning.main} />
            <span className="text-sm font-inter-medium" style={{ color: Colors.warning.main }}>
              Your session has expired due to inactivity. Please sign in again.
            </span>
          </div>
        )}

        {/* Error Message */}
        {displayError && (
          <div 
            className="flex items-center space-x-2 p-3 mb-4 rounded-md border"
            style={{ 
              backgroundColor: Colors.error.main + '10', 
              borderColor: Colors.error.main 
            }}
          >
            <AlertCircle size={16} color={Colors.error.main} />
            <span className="text-sm font-inter-medium" style={{ color: Colors.error.main }}>
              {displayError}
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username Input */}
          <div>
            <div 
              className="flex items-center space-x-3 p-3 rounded-md"
              style={{ backgroundColor: Colors.background.default }}
            >
              <User size={20} color={Colors.text.secondary} />
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value)
                  setLocalError('')
                  setShowSessionExpiredMessage(false)
                }}
                className="flex-1 bg-transparent outline-none font-inter-regular"
                style={{ color: Colors.text.primary }}
                disabled={isLoading}
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>
          </div>

          {/* Password Input */}
          <div>
            <div 
              className="flex items-center space-x-3 p-3 rounded-md"
              style={{ backgroundColor: Colors.background.default }}
            >
              <Lock size={20} color={Colors.text.secondary} />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setLocalError('')
                  setShowSessionExpiredMessage(false)
                }}
                className="flex-1 bg-transparent outline-none font-inter-regular"
                style={{ color: Colors.text.primary }}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full p-3 rounded-md font-inter-semibold text-white transition-opacity"
            style={{ 
              backgroundColor: Colors.primary.main,
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Footer */}
        <div className="flex justify-center items-center space-x-1 mt-6">
          <span className="text-sm font-inter-regular" style={{ color: Colors.text.secondary }}>
            Don't have an account?
          </span>
          <button 
            onClick={handleContactAdministrator}
            className="text-sm font-inter-semibold hover:underline" 
            style={{ color: Colors.primary.main }}
          >
            Contact Administrator
          </button>
        </div>

        {/* Security Notice */}
        <div 
          className="flex items-center space-x-2 p-3 mt-4 rounded-md"
          style={{ backgroundColor: Colors.neutral[100] }}
        >
          <Clock size={14} color={Colors.text.secondary} />
          <span className="text-xs font-inter-regular" style={{ color: Colors.text.secondary }}>
            For security, you'll be automatically signed out after 5 minutes of inactivity
          </span>
        </div>
      </div>
    </div>
  )
}